import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, X } from "lucide-react";
import Select from "react-select";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { createPayrollRun } from "../../../api/hrm/payroll/payroll";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";

const fieldSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...customSelectStyles.control(provided, state),
    minHeight: "42px",
    borderRadius: "12px",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const SalaryRunCreateModal = ({ isOpen, onClose, onCreated }) => {
  const [yearMonth, setYearMonth] = useState(getCurrentMonth());
  const [branchId, setBranchId] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setYearMonth(getCurrentMonth());
    setBranchId("");
    getAllBranches_Array().then((items) =>
      setBranchOptions(items.map((b) => ({ value: b.id, label: b.name || "—" })))
    );
  }, [isOpen]);

  const selectedBranch = branchOptions.find((o) => o.value === branchId) || null;

  const handleSubmit = async () => {
    if (!yearMonth) {
      return showErrorToast("Vui lòng chọn tháng lương.");
    }
    try {
      setSaving(true);
      const res = await createPayrollRun({
        year_month: yearMonth,
        branch_id: branchId || null,
      });
      showSuccessToast("Đã tạo kỳ lương.");
      onCreated?.(res?.run?.id || null);
      onClose?.();
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Không thể tạo kỳ lương.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[61] p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-100">
              <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-black text-slate-800">Tạo kỳ lương</h3>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Chọn tháng đã chốt công để khởi tạo bảng lương
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-slate-400 hover:text-red-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Tháng lương
                  </label>
                  <input
                    type="month"
                    value={yearMonth}
                    onChange={(e) => setYearMonth(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium focus:border-red-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Chi nhánh (tùy chọn)
                  </label>
                  <Select
                    value={selectedBranch}
                    onChange={(opt) => setBranchId(opt?.value || "")}
                    options={branchOptions}
                    styles={fieldSelectStyles}
                    menuPortalTarget={document.body}
                    isClearable
                    placeholder="Toàn công ty"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl font-bold text-slate-500"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center gap-2 disabled:opacity-70"
                >
                  {saving ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={16} />
                  )}
                  Tạo kỳ lương
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SalaryRunCreateModal;
